import { levelAt, mapInfo, worldToRadar, type Level, type MapId } from "@69ly/maps";
import type { NotePin, Replay, ReplayGrenade } from "@69ly/shared";
import { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useRadarView } from "../components/Radar.tsx";
import { cs2Image, useCs2Icons } from "./cs2-icons.tsx";
import { bombAt, deathsAt, grenadesAt, killsAt, playersAt, SHOT_REACH, shotsAt, type RoundIndex } from "./scene.ts";

const SIDE = { t: "#e8b14a", ct: "#5ab0ff" } as const;
/** Notes on the map: a colour of their own, apart from the sides and the grenades. */
const NOTE = "#c58cff";

/** How long a note's pin stays on the map once its moment has played, in game seconds. */
export const PIN_SECONDS = 6;

/** A note's pin to draw; `draft` while it is being placed. */
export interface MapPin {
  pin: NotePin;
  text: string;
  draft: boolean;
}
const INK = "#0b0f14";
/** World sizes, in game units. */
const SMOKE_RADIUS = 144;
const FIRE_RADIUS = 120;
const GRENADE_TRAIL: Record<string, string> = { smoke: "#d9dee4", flash: "#fff6b0", he: "#ff6b5a", molotov: "#ff9a3c", decoy: "#9aa4ae" };

/**
 * The replay drawn over the radar: a canvas in the radar's unzoomed overlay, so
 * players and grenades keep their size and stay sharp at any zoom. Redrawn when
 * the moment, the zoom or the round changes (the page animates the moment).
 */
export function ReplayCanvas({
  map,
  replay,
  round,
  tick,
  level,
  hasLevels,
  followed,
  numbers,
  labels: stuffNames,
  pins = NO_PINS,
  names = true,
}: {
  map: MapId;
  replay: Replay;
  round: RoundIndex;
  tick: number;
  level: Level;
  hasLevels: boolean;
  followed: number | null;
  /** Each player's number within their team (1–5), by index. */
  numbers: number[];
  /** Grenades that are stuffs of the library: the stuff's name, shown where it goes off. */
  labels?: ReadonlyMap<ReplayGrenade, string>;
  /** Notes' pins shown now. */
  pins?: readonly MapPin[];
  /** Each player's name beside their weapon (else only when zoomed in or followed). */
  names?: boolean;
}) {
  const { width: viewWidth, scale: viewScale, angle, toScreen, canvasLayer: overlay, bleed } = useRadarView();
  // The canvas covers the area the radar spills into (or just the square): drawn from the square's corner.
  const cw = viewWidth + bleed.left + bleed.right;
  const ch = viewWidth + bleed.top + bleed.bottom;
  const iconsReady = useCs2Icons();
  const canvas = useRef<HTMLCanvasElement>(null);
  const size = useRef({ w: 0, h: 0, dpr: 1 });

  useLayoutEffect(() => {
    const c = canvas.current;
    if (!c || !viewWidth) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    if (size.current.w !== cw || size.current.h !== ch || size.current.dpr !== dpr) {
      c.width = Math.round(cw * dpr);
      c.height = Math.round(ch * dpr);
      size.current = { w: cw, h: ch, dpr };
    }
  });

  useEffect(() => {
    const c = canvas.current;
    const g = c?.getContext("2d");
    if (!c || !g || !viewWidth) return;
    const { dpr } = size.current;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, cw, ch);
    g.translate(bleed.left, bleed.top);
    const worldPx = (viewWidth * viewScale) / (1024 * mapInfo(map).scale);
    const at = (x: number, y: number) => {
      const p = worldToRadar(map, x, y);
      return toScreen(p.u, p.v);
    };
    const onLevel = (z: number) => !hasLevels || levelAt(map, z) === level;

    // Grenades: trails, smokes and fires under the players.
    for (const gv of grenadesAt(replay, round, tick)) {
      const here = onLevel(gv.z);
      g.globalAlpha = here ? 1 : 0.3;
      if (gv.trail.length >= 6) {
        g.beginPath();
        for (let i = 0; i < gv.trail.length; i += 3) {
          const p = at(gv.trail[i], gv.trail[i + 1]);
          if (i === 0) g.moveTo(p.x, p.y);
          else g.lineTo(p.x, p.y);
        }
        g.strokeStyle = GRENADE_TRAIL[gv.kind];
        g.globalAlpha = (here ? 1 : 0.3) * (gv.state === "flying" ? 0.85 : 0.35);
        g.lineWidth = 1.5;
        g.setLineDash(gv.state === "flying" ? [] : [3, 3]);
        g.stroke();
        g.setLineDash([]);
      }
      const p = at(gv.x, gv.y);
      g.globalAlpha = here ? 1 : 0.3;
      if (gv.state === "flying") {
        g.beginPath();
        g.arc(p.x, p.y, 3.5, 0, Math.PI * 2);
        g.fillStyle = GRENADE_TRAIL[gv.kind];
        g.fill();
        g.lineWidth = 1;
        g.strokeStyle = INK;
        g.stroke();
      } else if (gv.state === "effect" && (gv.kind === "smoke" || gv.kind === "molotov")) {
        const radius = (gv.kind === "smoke" ? SMOKE_RADIUS : FIRE_RADIUS) * worldPx;
        g.beginPath();
        g.arc(p.x, p.y, Math.max(6, radius), 0, Math.PI * 2);
        g.fillStyle = gv.kind === "smoke" ? `rgba(205, 211, 218, ${0.62 * gv.strength})` : `rgba(255, 112, 40, ${0.38 * gv.strength})`;
        g.fill();
        g.lineWidth = 1.5;
        g.strokeStyle = gv.kind === "smoke" ? `rgba(240, 244, 248, ${0.8 * gv.strength})` : `rgba(255, 160, 60, ${0.9 * gv.strength})`;
        g.stroke();
        if (gv.left > 0 && radius > 11) {
          g.fillStyle = INK;
          g.font = `600 ${radius > 18 ? 11 : 9}px Inter, system-ui, sans-serif`;
          g.textAlign = "center";
          g.fillText(String(Math.ceil(gv.left)), p.x, p.y + 4);
        }
      } else if (gv.state === "burst" && gv.strength > 0) {
        const r = (gv.kind === "he" ? 60 : 90) * worldPx * (1.2 - gv.strength * 0.4);
        g.beginPath();
        g.arc(p.x, p.y, Math.max(5, r), 0, Math.PI * 2);
        g.fillStyle = gv.kind === "he" ? `rgba(255, 90, 60, ${0.55 * gv.strength})` : `rgba(255, 255, 230, ${0.75 * gv.strength})`;
        g.fill();
      }
    }
    g.globalAlpha = 1;

    // A stuff of the library: its name where it goes off, while it lasts.
    if (stuffNames?.size) {
      g.font = "600 10px Inter, system-ui, sans-serif";
      g.textAlign = "center";
      g.lineWidth = 3;
      for (const gv of grenadesAt(replay, round, tick)) {
        const name = stuffNames.get(gv.grenade);
        if (!name || (gv.state !== "effect" && gv.state !== "burst" && gv.state !== "flying")) continue;
        const p = at(gv.x, gv.y);
        const y = gv.state === "effect" && gv.kind !== "flash" ? p.y - Math.max(10, (gv.kind === "smoke" ? SMOKE_RADIUS : FIRE_RADIUS) * worldPx) - 4 : p.y - 10;
        g.globalAlpha = onLevel(gv.z) ? 1 : 0.35;
        g.strokeStyle = "rgba(11, 15, 20, 0.9)";
        g.strokeText(`✓ ${name}`, p.x, y);
        g.fillStyle = "#7bd88f";
        g.fillText(`✓ ${name}`, p.x, y);
      }
      g.globalAlpha = 1;
    }

    // Where players died.
    for (const d of deathsAt(round, tick)) {
      const p = at(d.x, d.y);
      g.globalAlpha = onLevel(d.z) ? 0.7 : 0.25;
      g.strokeStyle = replay.players[d.victim] ? SIDE[sideOf(replay, round, d.victim)] : "#999";
      g.lineWidth = 2;
      g.beginPath();
      g.moveTo(p.x - 4, p.y - 4);
      g.lineTo(p.x + 4, p.y + 4);
      g.moveTo(p.x + 4, p.y - 4);
      g.lineTo(p.x - 4, p.y + 4);
      g.stroke();
    }
    g.globalAlpha = 1;

    const players = playersAt(replay, round, tick);
    const byIndex = new Map(players.map((p) => [p.index, p]));

    // Kill lines: killer to victim, fading.
    for (const { kill, age } of killsAt(replay, round, tick)) {
      const killer = byIndex.get(kill.killer);
      const victim = round.deaths.find((d) => d.tick === kill.tick && d.victim === kill.victim);
      if (!killer || !victim) continue;
      const a = at(killer.x, killer.y);
      const b = at(victim.x, victim.y);
      g.globalAlpha = Math.max(0, 1 - age / 1.6);
      g.strokeStyle = "#ff5a5a";
      g.lineWidth = 1.5;
      g.beginPath();
      g.moveTo(a.x, a.y);
      g.lineTo(b.x, b.y);
      g.stroke();
    }
    g.globalAlpha = 1;

    // Tracers: to the player the shot hit, or on through the map (no walls in 2D), fading.
    for (const shot of shotsAt(replay, round, tick)) {
      const p = byIndex.get(shot.player);
      if (!p || !p.alive) continue;
      const a = at(p.x, p.y);
      const target = shot.hit >= 0 ? byIndex.get(shot.hit) : undefined;
      const rad = (p.yaw * Math.PI) / 180;
      const end = target ? at(target.x, target.y) : at(p.x + Math.cos(rad) * SHOT_REACH, p.y + Math.sin(rad) * SHOT_REACH);
      const len = Math.hypot(end.x - a.x, end.y - a.y) || 1;
      const ux = (end.x - a.x) / len;
      const uy = (end.y - a.y) / len;
      const from = { x: a.x + ux * 9, y: a.y + uy * 9 };
      const fade = Math.max(0, 1 - shot.age / 0.12) * (onLevel(p.z) ? 1 : 0.3);
      const line = g.createLinearGradient(from.x, from.y, end.x, end.y);
      line.addColorStop(0, `rgba(255, 243, 196, ${0.95 * fade})`);
      line.addColorStop(1, `rgba(255, 243, 196, ${(target ? 0.8 : 0.05) * fade})`);
      g.strokeStyle = line;
      g.lineWidth = target ? 1.4 : 1.1;
      g.beginPath();
      g.moveTo(from.x, from.y);
      g.lineTo(end.x, end.y);
      g.stroke();
    }
    g.globalAlpha = 1;

    // The bomb, on the ground or planted.
    const bomb = bombAt(replay, round, tick);
    if (bomb.state === "dropped" || bomb.state === "planted") {
      const p = at(bomb.x, bomb.y);
      g.globalAlpha = onLevel(bomb.z) ? 1 : 0.35;
      const pulse = bomb.state === "planted" ? 0.5 + 0.5 * Math.sin(tick / 6) : 0;
      if (bomb.state === "planted") {
        g.beginPath();
        g.arc(p.x, p.y, 10 + pulse * 4, 0, Math.PI * 2);
        g.fillStyle = `rgba(255, 70, 70, ${0.25 + pulse * 0.2})`;
        g.fill();
      }
      g.fillStyle = bomb.state === "planted" ? "#ff4646" : "#f2a33a";
      g.strokeStyle = INK;
      g.lineWidth = 1.5;
      g.beginPath();
      g.rect(p.x - 5, p.y - 4, 10, 8);
      g.fill();
      g.stroke();
      g.globalAlpha = 1;
    }

    // Players: view wedge, disc with health ring, number, name.
    const labels = viewScale >= 1.4;
    const sorted = [...players].sort((a, b) => Number(a.index === followed) - Number(b.index === followed));
    for (const p of sorted) {
      if (!p.alive) continue;
      const a = at(p.x, p.y);
      const here = onLevel(p.z);
      const focus = p.index === followed;
      g.globalAlpha = here ? 1 : 0.35;
      // Screen angles follow the map's turn.
      const rad = ((angle - p.yaw) * Math.PI) / 180;
      const spread = focus ? 0.55 : 0.4;
      const reach = focus ? 54 : 30;
      const wedge = g.createRadialGradient(a.x, a.y, 6, a.x, a.y, reach);
      wedge.addColorStop(0, SIDE[p.side] + (focus ? "88" : "66"));
      wedge.addColorStop(1, SIDE[p.side] + "00");
      g.fillStyle = wedge;
      g.beginPath();
      g.moveTo(a.x, a.y);
      g.arc(a.x, a.y, reach, rad - spread, rad + spread);
      g.closePath();
      g.fill();
      g.beginPath();
      g.arc(a.x, a.y, 8, 0, Math.PI * 2);
      g.fillStyle = SIDE[p.side];
      g.fill();
      g.lineWidth = focus ? 2.5 : 1.5;
      g.strokeStyle = focus ? "#ffffff" : INK;
      g.stroke();
      // Health: the ring around the disc.
      if (p.hp < 100) {
        g.beginPath();
        g.arc(a.x, a.y, 10.5, -Math.PI / 2, -Math.PI / 2 + (Math.PI * 2 * p.hp) / 100);
        g.strokeStyle = p.hp > 50 ? "#7bd88f" : p.hp > 20 ? "#f4b860" : "#ff5a5a";
        g.lineWidth = 2;
        g.stroke();
      }
      g.fillStyle = INK;
      g.font = "700 10px Inter, system-ui, sans-serif";
      g.textAlign = "center";
      g.fillText(String(numbers[p.index] ?? ""), a.x, a.y + 3.5);
      // Blinded: the disc washed white, a glow, and an arc for the seconds left (full at 3 s).
      if (p.blind > 0) {
        const k = Math.min(1, p.blind / 2);
        const glow = g.createRadialGradient(a.x, a.y, 4, a.x, a.y, 22);
        glow.addColorStop(0, `rgba(255, 255, 255, ${0.95 * k})`);
        glow.addColorStop(1, "rgba(255, 255, 255, 0)");
        g.fillStyle = glow;
        g.beginPath();
        g.arc(a.x, a.y, 22, 0, Math.PI * 2);
        g.fill();
        g.beginPath();
        g.arc(a.x, a.y, 14, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.min(1, p.blind / 3));
        g.strokeStyle = "#ffffff";
        g.lineWidth = 2.5;
        g.stroke();
      }
      // The bomb's carrier: the C4 at the top right of the disc.
      if (p.bomb) {
        const c4 = iconsReady ? cs2Image("c4") : null;
        if (c4 && c4.naturalHeight) {
          const h = 12;
          const w = (c4.naturalWidth / c4.naturalHeight) * h;
          g.fillStyle = "rgba(11, 15, 20, 0.85)";
          g.beginPath();
          g.roundRect?.(a.x + 7, a.y - 19, w + 6, h + 4, 4);
          g.fill();
          g.drawImage(c4, a.x + 10, a.y - 17, w, h);
        } else {
          g.fillStyle = "#f2a33a";
          g.fillRect(a.x + 6, a.y - 12, 7, 6);
        }
      }
      // Under the disc: the weapon in hand, and the name beside it.
      const icon = iconsReady ? cs2Image(p.weapon) : null;
      const ih = focus ? 10 : 9;
      const iw = icon && icon.naturalHeight ? (icon.naturalWidth / icon.naturalHeight) * ih : 0;
      const showName = names || labels || focus;
      g.font = `${focus ? 700 : 600} 10.5px Inter, system-ui, sans-serif`;
      const nw = showName ? g.measureText(p.name).width : 0;
      const gap = iw && nw ? 4 : 0;
      const left = a.x - (iw + gap + nw) / 2;
      const y = a.y + 15;
      if (iw) {
        g.globalAlpha = here ? 0.95 : 0.3;
        g.drawImage(icon!, left, y, iw, ih);
        g.globalAlpha = here ? 1 : 0.35;
      }
      if (showName) {
        g.textAlign = "left";
        g.lineWidth = 3;
        g.lineJoin = "round";
        g.strokeStyle = "rgba(11, 15, 20, 0.88)";
        g.strokeText(p.name, left + iw + gap, y + ih - 1);
        g.fillStyle = p.blind > 0 ? "#ffffff" : focus ? "#ffffff" : "rgba(255, 255, 255, 0.9)";
        g.fillText(p.name, left + iw + gap, y + ih - 1);
        g.textAlign = "center";
      }
    }
    g.globalAlpha = 1;

    // Notes' pins, over everything.
    const unitPx = worldPx;
    for (const { pin, text, draft } of pins) {
      g.globalAlpha = "level" in pin && pin.level && hasLevels && pin.level !== level ? 0.35 : 1;
      g.strokeStyle = NOTE;
      g.fillStyle = NOTE;
      g.lineWidth = 2;
      g.setLineDash(draft ? [5, 4] : []);
      let anchor: { x: number; y: number } | null = null;
      if (pin.kind === "player") {
        const p = byIndex.get(pin.player);
        if (p?.alive) {
          anchor = at(p.x, p.y);
          g.beginPath();
          g.arc(anchor.x, anchor.y, 15, 0, Math.PI * 2);
          g.stroke();
        }
      } else if (pin.kind === "area") {
        anchor = at(pin.x, pin.y);
        g.beginPath();
        g.arc(anchor.x, anchor.y, Math.max(6, pin.r * unitPx), 0, Math.PI * 2);
        g.globalAlpha *= 0.25;
        g.fill();
        g.globalAlpha /= 0.25;
        g.stroke();
      } else {
        anchor = at(pin.x, pin.y);
        if (pin.kind === "view" && (pin.tx !== pin.x || pin.ty !== pin.y)) {
          const b = at(pin.tx, pin.ty);
          const ang = Math.atan2(b.y - anchor.y, b.x - anchor.x);
          g.beginPath();
          g.moveTo(anchor.x, anchor.y);
          g.lineTo(b.x, b.y);
          g.stroke();
          g.setLineDash([]);
          g.beginPath();
          g.moveTo(b.x, b.y);
          g.lineTo(b.x - Math.cos(ang - 0.45) * 10, b.y - Math.sin(ang - 0.45) * 10);
          g.lineTo(b.x - Math.cos(ang + 0.45) * 10, b.y - Math.sin(ang + 0.45) * 10);
          g.closePath();
          g.fill();
        }
        g.setLineDash([]);
        g.beginPath();
        g.arc(anchor.x, anchor.y, 6, 0, Math.PI * 2);
        g.fill();
        g.strokeStyle = INK;
        g.lineWidth = 1.5;
        g.stroke();
      }
      g.setLineDash([]);
      if (anchor && text) {
        const words = text.length > 34 ? `${text.slice(0, 33)}…` : text;
        g.font = "600 11px Inter, system-ui, sans-serif";
        g.textAlign = "center";
        g.lineWidth = 4;
        g.lineJoin = "round";
        g.strokeStyle = "rgba(11, 15, 20, 0.92)";
        g.strokeText(words, anchor.x, anchor.y - 18);
        g.fillStyle = NOTE;
        g.fillText(words, anchor.x, anchor.y - 18);
      }
    }
    g.globalAlpha = 1;
  }, [map, replay, round, tick, level, hasLevels, followed, numbers, stuffNames, viewWidth, viewScale, angle, toScreen, iconsReady, pins, names, cw, ch, bleed]);

  if (!overlay) return null;
  return createPortal(<canvas ref={canvas} className="replay-canvas" style={{ left: 0, top: 0, width: cw, height: ch }} aria-hidden="true" />, overlay);
}

const NO_PINS: readonly MapPin[] = [];

/** A dead player's side this round: the side of their team. */
function sideOf(replay: Replay, round: RoundIndex, player: number): "t" | "ct" {
  const team = replay.players[player]?.team ?? 0;
  return team === round.round.tTeam ? "t" : "ct";
}
